import type { Audience } from '@/cms/topics';

/**
 * The GA4 event contract. Every analytics call in the app goes through `track`,
 * so this union is the single place an event name or payload is defined — GTM
 * reads `module_key`, `question_id`, `language` and `audience` as custom
 * dimensions, which is what lets SLSA report per-question by language.
 *
 * Snake case is deliberate: these keys reach GA4 verbatim.
 */
export type AnalyticsEvent =
  | { event: 'language_selected'; language: string }
  | { event: 'onboarding_complete'; language: string; audience: Audience }
  | { event: 'topic_start'; module_key: string; language: string; audience: Audience }
  | {
      event: 'quiz_answer';
      module_key: string;
      language: string;
      audience: Audience;
      question_id: string;
      question_index: number;
      question_type: QuestionType;
      correct: boolean;
      /**
       * 1 for the first go, 2 for the retry, and so on. A `discovery` reports
       * one event per entry the learner opened, so its `attempt` is the
       * position in the set rather than a second try at the same question.
       */
      attempt: number;
    }
  | {
      event: 'topic_complete';
      module_key: string;
      language: string;
      audience: Audience;
      /** The star rating to one decimal place (`roundScore`), as the learner saw it. */
      score: number;
    }
  | { event: 'topic_abandon'; module_key: string; language: string; checkpoint_index: number }
  | { event: 'completion'; language: string; audience: Audience }
  | { event: 'passport_download'; language: string; audience: Audience }
  | { event: 'passport_share'; language: string; audience: Audience };

/** The answerable slice kinds, as reported to GA4. */
export type QuestionType = 'quiz' | 'text_checklist' | 'user_interaction' | 'activity' | 'discovery';

export type EventName = AnalyticsEvent['event'];

/** Every parameter any event carries, e.g. `module_key`. */
type EventParam = AnalyticsEvent extends infer E
  ? E extends AnalyticsEvent
    ? Exclude<keyof E, 'event'>
    : never
  : never;

/**
 * Every parameter set to `undefined`, pushed ahead of each event's own. GTM's
 * data layer is a persistent model that merges pushes, so without this a
 * `completion` would still carry the last `question_id` and GA4 would report
 * it. `Record` makes a new parameter a type error here until it is listed.
 */
const clearedParams: Record<EventParam, undefined> = {
  language: undefined,
  audience: undefined,
  module_key: undefined,
  question_id: undefined,
  question_index: undefined,
  question_type: undefined,
  correct: undefined,
  attempt: undefined,
  score: undefined,
  checkpoint_index: undefined,
};

/** The payload of one event, without the discriminant. */
export type EventPayload<N extends EventName> = Omit<Extract<AnalyticsEvent, { event: N }>, 'event'>;

declare global {
  interface Window {
    /** Created by the GTM snippet in `root.tsx`, before any route script runs. */
    dataLayer?: Array<AnalyticsEvent | Record<string, unknown>>;
  }
}

/**
 * Pushes one event onto the GTM data layer.
 *
 *   track('language_selected', { language: 'ar' });
 *
 * A missing `dataLayer` — no GTM id configured, an ad blocker, a test — is a
 * no-op rather than a throw, because analytics must never break a screen.
 */
export function track<N extends EventName>(name: N, payload: EventPayload<N>): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { dataLayer } = window;

  if (!dataLayer) {
    if (import.meta.env.DEV) {
      console.warn(`[analytics] dropped "${name}": window.dataLayer is not available`);
    }

    return;
  }

  dataLayer.push({ ...clearedParams, event: name, ...payload });
}
